import React from 'react';
import { motion } from 'motion/react';
import {
  WidgetDataResponse,
  WidgetDefinition,
} from '../../../../shared/types/widget';
import useWidgetRegistry from '../hooks/useWidgetRegistry';
import WidgetErrorBoundary from './WidgetErrorBoundary';
import useWidgetDataQuery from '../hooks/useWidgetDataQuery';
import { NoDataError } from '../errors';
import { assertWidgetContentRenderable } from '../utils';
import useFilters from '../../../../utilities/hooks/useFilters';
import { transformFilterKeysToQueryParams } from '../../../../shared/service/widgetApi';

type Props = {
  widget: WidgetDefinition;
  isGroupedByTabView?: boolean;
};

/**
 * WidgetContent - Data-fetching component using Suspense
 *
 * This component is intentionally separated from WidgetRenderer to enable
 * viewport-based lazy loading. Here's why:
 *
 * PROBLEM: useSuspenseQuery doesn't support an `enabled` parameter.
 * It always fetches immediately when the component mounts.
 *
 * SOLUTION: Only mount this component when the widget is in the viewport.
 * By lifting the visibility check to the parent (WidgetRenderer), we control
 * WHEN this component mounts, and therefore WHEN the query executes.
 *
 * FLOW:
 * 1. WidgetRenderer detects widget enters viewport
 * 2. WidgetRenderer conditionally renders WidgetContent
 * 3. WidgetContent mounts → useSuspenseQuery fires → component suspends
 * 4. Parent Suspense boundary shows fallback while loading
 * 5. Data arrives → WidgetContent renders (or throws NoDataError if the
 *    backend returned null data, caught by WidgetErrorBoundary)
 *
 * This ensures:
 * - No wasted network requests for off-screen widgets
 * - No loading state management (Suspense handles it)
 */
export default function WidgetContent({ widget, isGroupedByTabView }: Props) {
  const { getFilters } = useFilters();

  const appliedFiltersKey = JSON.stringify(
    transformFilterKeysToQueryParams(getFilters()),
  );

  return (
    <WidgetErrorBoundary widgetType={widget.type} resetKey={appliedFiltersKey}>
      <WidgetContentInner
        widget={widget}
        isGroupedByTabView={isGroupedByTabView}
      />
    </WidgetErrorBoundary>
  );
}

/**
 * The data-fetching/rendering logic lives here, inside WidgetErrorBoundary's
 * children, so that a thrown NoDataError (e.g. when the backend responds
 * with { data: null }) is caught by the boundary instead of crashing the
 * whole widget tree.
 */
function WidgetContentInner({ widget, isGroupedByTabView }: Props) {
  const { type: widgetType } = widget;
  const { getFilters } = useFilters();

  const { data: widgetData } = useWidgetDataQuery<typeof widgetType>(widget.id);

  if (!widgetData) {
    throw new NoDataError();
  }

  assertWidgetContentRenderable({
    widgetType,
    content: widgetData,
    appliedFilters: getFilters(),
  });

  const { component: Widget } = useWidgetRegistry({
    widgetType,
    version: widget.meta?.version ?? widgetData.meta?.version,
    isGroupedByTabView,
  });

  return (
    <motion.div
      key={widget.id}
      initial={{ opacity: 0, y: 3 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="h-full w-full"
    >
      <Widget definition={widget} content={widgetData} />
    </motion.div>
  );
}
